import React from 'react';
import { useNavigate } from 'react-router-dom';

import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

/** ヒーローのグラデーション定義（light / dark とグローカラー） */
export type HeroPalette = {
  /** light モードのグラデーション（120deg の 3 ストップ） */
  light: string;
  /** dark モードのグラデーション（120deg の 3 ストップ） */
  dark: string;
  /** 右上グローの rgb（'r,g,b' 形式） */
  glow: string;
};

type Props = {
  /** eyebrow（小さい英字ラベル・大文字） */
  eyebrow: string;
  /** 見出し（英語想定） */
  title: string;
  /** 説明（英語想定） */
  tagline: string;
  /** ドメインテーマ色に合わせたグラデーション */
  palette: HeroPalette;
  /**
   * 「ホームに戻る」押下時のハンドラ（任意）。
   * 未指定なら既定でメインメニュー（'/'）へ遷移する。
   * 全ドメインで共通の導線にするため、通常は指定不要。
   */
  onBack?: () => void;
  /**
   * 「ホームに戻る」リンクを表示するか（既定 true）。
   * レイアウト側で別途「ホームに戻る」を出す画面（DevInsights 等）では false にして重複を避ける。
   */
  showHome?: boolean;
  /**
   * 戻るボタンのラベル（既定 'ホームに戻る'）。
   * ドメインTop以外（詳細ダッシュボード等）でドメインTopへ戻す場合は '戻る' 等に変更する。
   */
  backLabel?: string;
  /**
   * 戻るボタンのアイコン（既定は家アイコン）。
   * ドメイン内の戻り導線では矢印アイコン（ArrowBackIcon）を渡す。
   */
  backIcon?: React.ReactNode;
  /**
   * 更新（ウィジェット最新化）押下時のハンドラ（任意）。
   * 指定した場合のみ、「ホームに戻る」行の右端に更新アイコンボタンを表示する。
   * 全ドメイン Home で共通の再取得導線にするためのスロット。
   */
  onRefresh?: () => void;
  /** 更新処理の実行中フラグ（任意）。true の間はスピナー表示 + 押下不可にする。 */
  refreshing?: boolean;
  /** 右側に置くアクション（任意） */
  actions?: React.ReactNode;
};

/**
 * 全ドメイン共通のヒーローヘッダー（研修 / DevInsights / 各ドメイン Home で共用）。
 * ドメインテーマ色に合わせたグラデーション + ドットグリッド + グロー。
 * onBack を渡した場合のみ左上に「ホームに戻る」ボタンを表示する（サイドバー非依存の導線）。
 */
const DomainHero: React.FC<Props> = ({
  eyebrow,
  title,
  tagline,
  palette,
  onBack,
  showHome = true,
  backLabel = 'ホームに戻る',
  backIcon = <HomeIcon sx={{ fontSize: 16 }} />,
  onRefresh,
  refreshing = false,
  actions,
}) => {
  const navigate = useNavigate();
  const handleHome = onBack ?? (() => navigate('/'));

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        px: { xs: 2.5, md: 4 },
        // 戻るボタンがある場合は上部を詰めてボタン分の余白を確保する
        pt: showHome ? { xs: 2, md: 2.5 } : { xs: 3, md: 3.5 },
        pb: { xs: 3, md: 3.5 },
        color: '#fff',
        background: (theme) => (theme.palette.mode === 'dark' ? palette.dark : palette.light),
      }}
    >
      {/* ドットグリッド */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px)',
          backgroundSize: '18px 18px',
          maskImage: 'linear-gradient(90deg, transparent 0%, #000 40%, #000 100%)',
          pointerEvents: 'none',
        }}
      />
      {/* 右上のグロー */}
      <Box
        sx={{
          position: 'absolute',
          top: -120,
          right: -80,
          width: 340,
          height: 340,
          borderRadius: '50%',
          background: `radial-gradient(circle, rgba(${palette.glow},0.35) 0%, rgba(${palette.glow},0) 70%)`,
          pointerEvents: 'none',
        }}
      />
      {/* 上部バー: 「ホームに戻る」(左) と 更新アイコン(右) を同じ高さで配置。
          showHome=false でも onRefresh があれば更新ボタンだけを右端に出す。 */}
      {(showHome || onRefresh) && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ position: 'relative', mb: 1, minHeight: 32 }}
        >
          {showHome ? (
            <Button
              size="small"
              startIcon={backIcon}
              onClick={handleHome}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                color: 'rgba(255,255,255,0.9)',
                px: 1,
                ml: -1,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
              }}
            >
              {backLabel}
            </Button>
          ) : (
            <Box />
          )}
          {onRefresh && (
            <Tooltip title="最新の状態に更新">
              <span>
                <IconButton
                  size="small"
                  onClick={onRefresh}
                  disabled={refreshing}
                  aria-label="最新の状態に更新"
                  sx={{
                    color: '#fff',
                    border: 1,
                    borderColor: 'rgba(255,255,255,0.45)',
                    borderRadius: 2,
                    p: 0.75,
                    transition: 'background-color 0.2s',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.6)' },
                  }}
                >
                  {refreshing ? (
                    <CircularProgress size={18} sx={{ color: 'inherit' }} />
                  ) : (
                    <RefreshIcon sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      )}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
        spacing={3}
        sx={{ position: 'relative' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Box sx={{ width: 22, height: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.6)' }} />
            <Typography
              sx={{
                fontSize: '0.62rem',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              {eyebrow}
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontSize: { xs: '1.6rem', md: '2rem' },
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontSize: '0.82rem',
              lineHeight: 1.7,
              color: 'rgba(255,255,255,0.85)',
            }}
          >
            {tagline}
          </Typography>
        </Box>
        {actions && <Box sx={{ flexShrink: 0, position: 'relative' }}>{actions}</Box>}
      </Stack>
    </Box>
  );
};

export default DomainHero;

/** ドメインごとのヒーローパレット（メニュー / DashboardPopup のテーマ色と対応） */
export const HERO_PALETTES: Record<string, HeroPalette> = {
  // セキュリティ #2e7d32（緑）
  security: {
    light: 'linear-gradient(120deg, #1b4d23 0%, #2e7d32 55%, #43a047 100%)',
    dark: 'linear-gradient(120deg, #0a1f0d 0%, #1b4d23 55%, #2e7d32 100%)',
    glow: '129,199,132',
  },
  // 要員管理 #1565c0（青）
  member: {
    light: 'linear-gradient(120deg, #0d3c73 0%, #1565c0 55%, #1e88e5 100%)',
    dark: 'linear-gradient(120deg, #061a33 0%, #0d3c73 55%, #1565c0 100%)',
    glow: '100,181,246',
  },
  // 契約管理 #6a1b9a（紫）
  contract: {
    light: 'linear-gradient(120deg, #40105e 0%, #6a1b9a 55%, #8e24aa 100%)',
    dark: 'linear-gradient(120deg, #1e0730 0%, #40105e 55%, #6a1b9a 100%)',
    glow: '186,104,200',
  },
  // 資産管理 #283593（藍）
  assessment: {
    light: 'linear-gradient(120deg, #171d54 0%, #283593 55%, #3949ab 100%)',
    dark: 'linear-gradient(120deg, #0a0e2e 0%, #171d54 55%, #283593 100%)',
    glow: '121,134,203',
  },
  // 管理者メニュー #00695c（ティール）
  management: {
    light: 'linear-gradient(120deg, #003b34 0%, #00695c 55%, #00897b 100%)',
    dark: 'linear-gradient(120deg, #041a17 0%, #003b34 55%, #00695c 100%)',
    glow: '77,182,172',
  },
  // 研修（ワークスペース / 研修受講 / 研修管理）#00838f 系（旧 TrainingHero と同一）
  training: {
    light: 'linear-gradient(120deg, #063b43 0%, #00838f 55%, #26a69a 100%)',
    dark: 'linear-gradient(120deg, #04141a 0%, #06373f 55%, #0a4f5a 100%)',
    glow: '77,208,225',
  },
  // DevInsights（記事）ネイビー系（旧 DevDocs ヒーローと同一）
  devdocs: {
    light: 'linear-gradient(120deg, #0f172a 0%, #1e3a8a 55%, #2563eb 100%)',
    dark: 'linear-gradient(120deg, #070d1f 0%, #101c40 55%, #12275e 100%)',
    glow: '96,165,250',
  },
  // チケット管理 #c2185b（マゼンタ系。既存ドメインと重複しない色相）
  ticket: {
    light: 'linear-gradient(120deg, #7a1338 0%, #c2185b 55%, #e91e63 100%)',
    dark: 'linear-gradient(120deg, #2e0714 0%, #6a1030 55%, #a01449 100%)',
    glow: '244,143,177',
  },
  // 経費管理 #2e7d32 系だと security と被るため、家計＝お金を想起する
  // アンバー/オレンジ系（#ef6c00）を採用（既存ドメインと重複しない色相）
  expense: {
    light: 'linear-gradient(120deg, #7a3b00 0%, #ef6c00 55%, #fb8c00 100%)',
    dark: 'linear-gradient(120deg, #2b1500 0%, #5c2c00 55%, #8f4300 100%)',
    glow: '255,167,38',
  },
};
